import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Store, Eye, EyeOff, CheckCircle, Loader2, ExternalLink, ArrowLeft, Sparkles, Crown, Mail } from 'lucide-react';

function CreatorApplicationModal({ isOpen, onClose, prefillData = {} }) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [vipStatus, setVipStatus] = useState(null); // null, 'approved', 'pending', 'checking'
  const [resendingLink, setResendingLink] = useState(false);
  const [formData, setFormData] = useState({
    name: prefillData.name || '',
    email: prefillData.email || '',
    password: prefillData.password || '',
    store_name: prefillData.store_name || '',
    portfolio_url: '',
    product_description: '',
    experience: '',
    terms_accepted: false,
    ownership_declared: false
  });

  // Check if email belongs to an approved VIP
  const checkVipStatus = async (email) => {
    if (!email || !email.includes('@')) return;
    
    setVipStatus('checking');
    try {
      const response = await axios.get(`/vip-creator-requests/check/${encodeURIComponent(email)}`);
      if (response.data.is_vip && response.data.status === 'approved') {
        setVipStatus('approved');
        setFormData(prev => ({ ...prev, name: response.data.name || prev.name }));
      } else if (response.data.is_vip && response.data.status === 'pending') {
        setVipStatus('pending');
      } else {
        setVipStatus(null);
      }
    } catch (error) {
      setVipStatus(null);
    }
  };

  // Resend VIP setup link
  const resendSetupLink = async () => {
    setResendingLink(true);
    try {
      await axios.post(`/vip-creator-requests/resend-by-email`, { email: formData.email });
      toast.success('Setup link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send setup link');
    } finally {
      setResendingLink(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.terms_accepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    if (!formData.ownership_declared) {
      toast.error('Please confirm the product ownership declaration');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post('/creator-applications', formData);
      setApplicationSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  // VIP Already Approved - Show special message
  if (vipStatus === 'approved') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-neutral-900 border-[#00F0FF]/30 text-white max-w-md">
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-br from-[#00F0FF] to-[#00FF94] rounded-full flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-black" />
            </div>
            <h2 className="text-2xl font-bold mb-3">You're Already Approved!</h2>
            <p className="text-neutral-400 mb-6">
              Great news! Your VIP Creator application has already been approved. Check your email for a link to set your password and access your creator dashboard.
            </p>
            <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4 text-left mb-6">
              <p className="text-sm text-neutral-300">
                <span className="text-[#00F0FF]">✓</span> VIP Status: <span className="text-[#00F0FF] font-semibold">Approved</span>
              </p>
              <p className="text-sm text-neutral-300 mt-1">
                <span className="text-[#00F0FF]">✓</span> Email: <span className="text-white">{formData.email}</span>
              </p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={resendSetupLink}
                disabled={resendingLink}
                className="w-full bg-gradient-to-r from-[#00F0FF] to-[#00FF94] hover:opacity-90 text-black font-bold rounded-full"
              >
                {resendingLink ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Setup Link
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setVipStatus(null);
                  setFormData(prev => ({ ...prev, email: '' }));
                }}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10 rounded-full"
              >
                Use Different Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Success screen after application submitted
  if (applicationSubmitted) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-br from-white/20 to-[#00F0FF]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-[#00F0FF]" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Application Submitted!</h2>
            <p className="text-neutral-400 mb-6">
              We&apos;ve received your creator application and will review it within 24-48 hours.
              You&apos;ll receive an email once it&apos;s approved.
            </p>
            <div className="bg-neutral-800 rounded-xl p-4 text-left space-y-2 mb-6">
              <p className="text-sm text-neutral-400">
                <span className="text-[#00F0FF]">✓</span> Application received for: <span className="text-white">{formData.email}</span>
              </p>
              <p className="text-sm text-neutral-400">
                <span className="text-[#00F0FF]">✓</span> Store name: <span className="text-white">{formData.store_name}</span>
              </p>
            </div>
            <Button
              onClick={onClose}
              className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold rounded-full"
            >
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <button
            onClick={onClose}
            className="absolute left-4 top-4 text-neutral-400 hover:text-white transition-colors flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="text-center pt-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#00F0FF] to-white rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Store className="w-8 h-8 text-white" />
            </div>
            <DialogTitle className="text-2xl font-bold">
              Become a Creator
            </DialogTitle>
            <p className="text-neutral-400 mt-2 text-sm">
              Apply to sell your AI products on theailedger.ai
            </p>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Basic Info Section */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Full Name *</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                className="bg-neutral-800 border-white/10"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                onBlur={(e) => checkVipStatus(e.target.value)}
                placeholder="you@example.com"
                className="bg-neutral-800 border-white/10"
                required
              />
              {vipStatus === 'checking' && (
                <p className="text-xs text-[#00F0FF] mt-1 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Checking VIP status...
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Password *</label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="bg-neutral-800 border-white/10 pr-10"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-neutral-500 mt-1">Min 6 characters. You&apos;ll use this to login when approved.</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Store / Brand Name *
            </label>
            <Input
              type="text"
              value={formData.store_name}
              onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
              placeholder="My AI Tools Store"
              className="bg-neutral-800 border-white/10"
              required
            />
            <p className="text-xs text-neutral-500 mt-1">This will be your public store name</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Portfolio / Website URL <span className="text-neutral-500 font-normal">(optional)</span>
            </label>
            <Input
              type="url"
              value={formData.portfolio_url}
              onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
              placeholder="https://your-portfolio.com"
              className="bg-neutral-800 border-white/10"
            />
            <p className="text-xs text-neutral-500 mt-1">Link to your work, social media, or existing store</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              What AI products do you plan to sell? *
            </label>
            <textarea
              value={formData.product_description}
              onChange={(e) => setFormData({ ...formData, product_description: e.target.value })}
              placeholder="E.g., I create AI writing tools, prompt templates, and automation scripts..."
              className="w-full bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Your experience with AI products *
            </label>
            <textarea
              value={formData.experience}
              onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
              placeholder="E.g., I've been developing AI tools for 2 years, sold on other platforms..."
              className="w-full bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50"
              required
            />
          </div>

          {/* Benefits */}
          <div className="bg-white text-black/10 border border-white/30 rounded-xl p-4">
            <h4 className="font-semibold text-sm text-white mb-3">What you get as an approved creator:</h4>
            <ul className="text-xs text-neutral-300 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00FF94]" />
                Access to Creator Dashboard & Analytics
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00FF94]" />
                Exclusive Creator Lounge community
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00FF94]" />
                Instant payouts via Stripe Connect
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#00FF94]" />
                Built-in affiliate program tools
              </li>
            </ul>
          </div>

          {/* Terms */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="creator-terms"
              checked={formData.terms_accepted}
              onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
              className="mt-1 w-4 h-4 rounded border-white/20 bg-neutral-800 text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0 cursor-pointer"
              required
            />
            <label htmlFor="creator-terms" className="text-sm text-neutral-400 cursor-pointer">
              I agree to the{' '}
              <button
                type="button"
                onClick={() => window.open('/terms', '_blank')}
                className="text-[#00F0FF] hover:underline inline-flex items-center gap-1"
              >
                Terms & Conditions <ExternalLink className="w-3 h-3" />
              </button>
              {' '}and{' '}
              <button
                type="button"
                onClick={() => window.open('/privacy', '_blank')}
                className="text-[#00F0FF] hover:underline inline-flex items-center gap-1"
              >
                Privacy Policy <ExternalLink className="w-3 h-3" />
              </button>
            </label>
          </div>

          {/* Product Ownership Declaration */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="ownership-declaration"
              checked={formData.ownership_declared}
              onChange={(e) => setFormData({ ...formData, ownership_declared: e.target.checked })}
              className="mt-1 w-4 h-4 rounded border-white/20 bg-neutral-800 text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0 cursor-pointer"
              required
            />
            <label htmlFor="ownership-declaration" className="text-sm text-neutral-400 cursor-pointer">
              <span className="text-white font-medium">I declare</span> that all products I list on theailedger.ai are either created by me, owned by me, or I have the legal authority and rights to sell them. I understand that selling unauthorized or stolen content may result in account termination and legal action.
            </label>
          </div>

          <Button
            type="submit"
            disabled={loading || !formData.terms_accepted || !formData.ownership_declared}
            className="w-full bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold rounded-full py-6 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting Application...
              </>
            ) : (
              'Submit Application'
            )}
          </Button>

          <p className="text-center text-xs text-neutral-500">
            Applications are typically reviewed within 24-48 hours
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CreatorApplicationModal;
